/* Hallmark · component: cookie consent banner · genre: editorial · theme: host_ui.css
 * states: hidden · visible (choice) · visible (customizing) · granted · denied
 *
 * Two independent categories, not one: Analytics (Google Analytics) and
 * Advertising (Meta Pixel) are gated on separate GTM consent signals
 * (analytics_storage / ad_storage), so accepting one never silently unlocks
 * the other. Visibility still keys off analytics alone — a visitor who
 * already answered before this category existed is not re-interrupted; their
 * unset advertising choice simply stays denied (the always-safe default)
 * until they reopen Cookie preferences themselves.
 */
import React, { useEffect, useState } from "react";

import { Button } from "@javascript/components/ui/button";
import { Switch } from "@javascript/components/ui/switch";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

export const ANALYTICS_CONSENT_STORAGE_KEY = "weddang-analytics-consent";
export const ADVERTISING_CONSENT_STORAGE_KEY = "weddang-advertising-consent";
export const COOKIE_PREFERENCES_EVENT = "weddang:cookie-preferences-open";

type ConsentChoice = "granted" | "denied";
type ConsentCategory = "analytics" | "advertising";
type Choices = Record<ConsentCategory, ConsentChoice>;

const STORAGE_KEY: Record<ConsentCategory, string> = {
  analytics: ANALYTICS_CONSENT_STORAGE_KEY,
  advertising: ADVERTISING_CONSENT_STORAGE_KEY,
};

const GTAG_KEY: Record<ConsentCategory, string> = {
  analytics: "analytics_storage",
  advertising: "ad_storage",
};

export function openCookiePreferences() {
  window.dispatchEvent(new Event(COOKIE_PREFERENCES_EVENT));
}

function readChoice(category: ConsentCategory): ConsentChoice | null {
  const recorded = window.localStorage.getItem(STORAGE_KEY[category]);
  return recorded === "granted" || recorded === "denied" ? recorded : null;
}

function pushConsentUpdate(choices: Choices) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push([
    "consent",
    "update",
    { analytics_storage: choices.analytics, ad_storage: choices.advertising },
  ]);
}

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState<ConsentChoice>("denied");
  const [advertising, setAdvertising] = useState<ConsentChoice>("denied");

  useEffect(() => {
    setVisible(readChoice("analytics") === null);
    setAnalytics(readChoice("analytics") ?? "denied");
    setAdvertising(readChoice("advertising") ?? "denied");

    const reopen = () => {
      setAnalytics(readChoice("analytics") ?? "denied");
      setAdvertising(readChoice("advertising") ?? "denied");
      setCustomizing(false);
      setVisible(true);
    };
    window.addEventListener(COOKIE_PREFERENCES_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, reopen);
  }, []);

  if (!visible) return null;

  const save = (choices: Choices) => {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, choices.analytics);
    window.localStorage.setItem(ADVERTISING_CONSENT_STORAGE_KEY, choices.advertising);
    pushConsentUpdate(choices);
    setVisible(false);
    setCustomizing(false);
  };

  return (
    <div
      data-cookie-consent-banner
      role="region"
      aria-label="Cookie preferences"
      className="shrink-0 border-t bg-popover px-4 py-4 text-popover-foreground sm:px-6"
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-2xl text-sm leading-relaxed">
            We use two kinds of cookies: <strong>Analytics</strong> (Google Analytics, to
            understand how Goldypaper is used) and <strong>Advertising</strong> (Meta Pixel, to
            measure our ads). Your guests&rsquo; RSVP page is never tracked either way. Read our{" "}
            <a
              href="/privacy"
              className="font-medium underline decoration-foreground/40 underline-offset-4 transition-colors hover:decoration-foreground"
            >
              Privacy Policy
            </a>
            .
          </p>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => save({ analytics: "denied", advertising: "denied" })}
            >
              Reject all
            </Button>
            {!customizing && (
              <Button type="button" variant="outline" onClick={() => setCustomizing(true)}>
                Customize
              </Button>
            )}
            <Button
              type="button"
              variant="default"
              onClick={() => save({ analytics: "granted", advertising: "granted" })}
            >
              Accept all
            </Button>
          </div>
        </div>

        {customizing && (
          <div className="flex flex-col gap-5 border-t pt-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-1 flex-col gap-4">
              <label className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-medium">Analytics</span>
                  <span className="block text-sm text-muted-foreground">
                    Google Analytics, to understand how Goldypaper is used.
                  </span>
                </span>
                <Switch
                  checked={analytics === "granted"}
                  onCheckedChange={(checked) => setAnalytics(checked ? "granted" : "denied")}
                  aria-label="Analytics cookies"
                />
              </label>
              <label className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-medium">Advertising</span>
                  <span className="block text-sm text-muted-foreground">
                    Meta Pixel, to measure our ads.
                  </span>
                </span>
                <Switch
                  checked={advertising === "granted"}
                  onCheckedChange={(checked) => setAdvertising(checked ? "granted" : "denied")}
                  aria-label="Advertising cookies"
                />
              </label>
            </div>
            <Button
              type="button"
              variant="default"
              className="shrink-0"
              onClick={() => save({ analytics, advertising })}
            >
              Save choices
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
